import React from 'react';
import { Box, Typography, Button, Stack, useTheme, keyframes } from '@mui/material';
import { Link } from 'react-router-dom';

const floatAnimation = keyframes`
  0% { transform: translate(0, 0); }
  50% { transform: translate(20px, -20px); }
  100% { transform: translate(0, 0); }
`;

const statements = [
  "DEI programs being cancelled",
  "Government announces new policy changes",
  "Stock markets reach new highs",
  "Breakthrough in renewable energy",
  "Major tech company launches new product",
  "Climate change conference draws record attendance",
  "Healthcare reforms spark debates",
];

function FlyingStatements() {
  const theme = useTheme();
  const statementColor = theme.palette.mode === "dark"
    ? "rgba(180, 36, 36, 0.8)"
    : "rgba(238, 62, 71, 0.54)";

  const positions = React.useMemo(() => {
    return statements.map(() => ({
      top: Math.random() * 80,
      left: Math.random() * 80,
      duration: 5 + Math.random() * 5,
      delay: Math.random() * 5
    }));
  }, []);

  return (
    <Box sx={{ position: 'absolute', width: '100%', height: '100%', pointerEvents: 'none' }}>
      {statements.map((text, index) => {
        const pos = positions[index];
        return (
          <Typography
            key={index}
            variant="subtitle1"
            sx={{
              position: 'absolute',
              top: `${pos.top}%`,
              left: `${pos.left}%`,
              whiteSpace: 'nowrap',
              animation: `${floatAnimation} ${pos.duration}s ease-in-out infinite`,
              animationDelay: `${pos.delay}s`,
              opacity: 0.8,
              color: statementColor,
              fontWeight: 500,
            }}
          >
            {text}
          </Typography>
        );
      })}
    </Box>
  );
}

export default function HomePage() {
  const theme = useTheme();

  const backgroundGradient =
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #1c1c1e, #3a3a3c)"
      : "linear-gradient(135deg, #FAFAFF, #d9e2ec)";

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background: backgroundGradient,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'hidden',
        overflowY: 'auto',
        p: 3,
      }}
    >
      {/* Floating Statements */}
      <FlyingStatements />

      {/* Logo */}
      <Box
        component="img"
        src="../xfactualLogo.png"
        alt="Xfactual Logo"
        sx={{
          width: 180,
          height: 180,
          mb: 2,
          zIndex: 1,
        }}
      />

      {/* Headline + Subheadline */}
      <Typography
        variant="h2"
        component="h1"
        align="center"
        gutterBottom
        sx={{ zIndex: 1, color: theme.palette.mode === "dark" ? "#FFF" : "#111", fontWeight: 700 }}
      >
        Instantly Fact-Check Any Claim 
      </Typography>
      <Typography
        variant="h6"
        align="center"
        maxWidth="700px"
        sx={{ zIndex: 1, color: theme.palette.mode === "dark" ? "#CCC" : "#333", mt: 1 }}
      >
        Xfactual delivers lightning-fast, AI-powered fact checks with context, ratings, and credible sources.
        --Beta Testing--
      </Typography>

      {/* CTA Buttons */}
      <Stack direction="row" spacing={2} mt={4} sx={{ zIndex: 1 }}>
        <Button variant="contained" color="primary" component={Link} to="/login">
          Try It Now
        </Button>
        <Button variant="outlined" color="primary" component={Link} to="/about">
          Learn More
        </Button>
      </Stack>

      {/* Screenshot Showcase */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 1000,
          backgroundColor: theme.palette.mode === 'dark' ? '#2b2b2b' : '#ffffff',
          mt: 10,
          px: 4,
          py: 6,
          borderRadius: 4,
          boxShadow: 3,
          zIndex: 1,
        }}
      >
        <Typography variant="h5" align="center" gutterBottom>
          See Xfactual in Action
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 4, color: theme.palette.text.secondary }}>
          Type in any claim. Get fast verdicts, justifications, and direct links to credible sources.
        </Typography>

        <Stack spacing={4}>
          <Box
            component="img"
            src="/screenshots/screenshot5.png"
            alt="Xfactual Claim Result"
            sx={{ width: '100%', borderRadius: 2, boxShadow: 2 }}
          />
          <Box
            component="img"
            src="/screenshots/screenshot10.png"
            alt="Xfactual News Section"
            sx={{ width: '100%', borderRadius: 2, boxShadow: 2 }}
          />
          <Box
            component="img"
            src="/screenshots/screenshot15.png"
            alt="Xfactual Sources Section"
            sx={{ width: '100%', borderRadius: 2, boxShadow: 2 }}
          />
        </Stack>
      </Box>

      {/* Feature Icon Row */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 1000,
          mt: 10,
          px: 3,
          py: 6,
          zIndex: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" gutterBottom>
          Why Use Xfactual?
        </Typography>
        <Typography variant="body1" sx={{ mb: 5, color: theme.palette.text.secondary }}>
          Everything you need to fact-check smarter and faster.
        </Typography>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          justifyContent="center"
          alignItems="stretch"
          sx={{ width: '100%' }}
        >
          {[
            {
              icon: "⚡",
              title: "Fast Results",
              desc: "Get instant fact-checks with clear verdicts."
            },
            {
              icon: "🧠",
              title: "AI-Powered",
              desc: "Combines large language models with fact databases for better accuracy."
            },
            {
              icon: "🔗",
              title: "Trusted Sources",
              desc: "Every result includes links to high-quality supporting evidence."
            },
            {
              icon: "🎯",
              title: "Simple to Use",
              desc: "Just paste a claim — we handle the rest."
            }
          ].map((item, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                minWidth: 220,
                backgroundColor: theme.palette.mode === 'dark' ? '#2b2b2b' : '#f9f9f9',
                borderRadius: 3,
                p: 3,
                boxShadow: 2,
              }}
            >
              <Typography variant="h3" component="div" gutterBottom>
                {item.icon}
              </Typography>
              <Typography variant="h6" gutterBottom>
                {item.title}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* How It Works Video */}
      <Box mt={8} sx={{ width: '100%', maxWidth: 900, zIndex: 1 }}>
        <Typography variant="h5" align="center" gutterBottom>
          How It Works
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 3, color: theme.palette.text.secondary }}>
          Watch a quick demo of Xfactual in action.
        </Typography>
        <video controls style={{ width: '100%', borderRadius: 12 }}>
          <source src="/screenshots/xfactual-demo.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </Box>
    </Box>
  );
}
